import { defineStore } from 'pinia';
import { apiScope, getMsal } from '@/auth/msal';

interface AuthState {
  email: string | null;
  name: string | null;
  claims: Record<string, any> | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({ email: null, name: null, claims: null }),
  actions: {
    syncFromMsal() {
      const account = getMsal().getAllAccounts()[0];
      this.email = account?.username ?? null;
      this.name = account?.name ?? null;
      this.claims = (account?.idTokenClaims as Record<string, any>) ?? null;
    },
    // Kicks off the MSAL redirect flow. Called from the landing page's Sign-in button. The
    // redirect takes over the tab, so this never resolves on success — only on error (e.g.
    // an interaction already in progress).
    async signIn() {
      await getMsal().loginRedirect({ scopes: [apiScope()] });
    },
    // Local-only sign-out. Returning false from onRedirectNavigate cancels the trip to
    // the AAD end-session endpoint, so MSAL clears its own cache (tokens + account) but
    // Microsoft's "pick an account to sign out of" page never renders. We then hard-
    // navigate to the SPA root so MSAL re-initialises with no account and LandingView
    // shows the logged-out state.
    //
    // Caveat: this does NOT clear the Azure AD SSO session cookie — only the app's
    // local session. A subsequent "Sign in" may silently SSO back in without a
    // credential prompt. Acceptable for an idle/screen-lock logout; if full credential
    // re-entry is ever required, restore the federated logout (msal.logoutRedirect with
    // no onRedirectNavigate) and accept the account-picker.
    async signOut() {
      const msal = getMsal();
      const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? undefined;
      await msal.logoutRedirect({
        account,
        onRedirectNavigate: () => false,
      });
      window.location.assign(`${window.location.origin}/admin/`);
    },
  },
});
